"use client";
import React, { forwardRef, useCallback } from "react";
import defaultStyles from "./main.module.scss";

type ChangeEvent = React.ChangeEvent<HTMLInputElement>;
type EventArgs = {
  name: ChangeEvent["target"]["name"];
  value: ChangeEvent["target"]["value"];
  checked: ChangeEvent["target"]["checked"];
  _e: ChangeEvent;
};

export interface CheckboxInputProps
  extends Omit<React.ComponentPropsWithoutRef<"input">, "onChange"> {
  onChange: (options: EventArgs) => void | EventArgs;
  /**
   * Make sure to pass only Label instance from ui/src/components/form/Label
   */
  label?: React.ReactElement<any>;
  /**
   * These classes are appended to the parent container
   */
  classes?: string;
  /**
   * If the form field has error, pass this as true
   */
  error?: boolean;
  styles?: ModularStyles;
}

const CheckboxInput = forwardRef<HTMLInputElement, CheckboxInputProps>(
  (
    {
      onChange: propsOnChange = null,
      onClick = null,
      label = null,
      placeholder = "",
      className = "",
      classes = "",
      id = `input-${Date.now()}`,
      name = "checkbox-input",
      value = "",
      checked = false,
      disabled = false,
      children,
      styles = defaultStyles,
    },
    ref,
  ) => {
    const onChange = useCallback(
      (e) =>
        propsOnChange?.({
          name: e.target.name,
          value: e.target.value,
          checked: e.target.checked,
          _e: e,
        }),
      [propsOnChange],
    );
    return (
      <div className={`${styles.checkboxInput} ${classes}`}>
        <input
          type="checkbox"
          placeholder={placeholder}
          className={className}
          name={name}
          id={id}
          ref={ref}
          disabled={disabled}
          value={value}
          checked={checked}
          onChange={onChange}
          onClick={onClick}
        />
        {label &&
          React.cloneElement(label, { checked, disabled, htmlFor: id, "aria-disabled": disabled })}
        {children}
      </div>
    );
  },
);

export default CheckboxInput;
